import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Save, Loader2, RotateCcw } from "lucide-react";
import { Hospital, CreateDoctorPayload, DoctorProfile } from "../types";
import { createDoctor, updateDoctorData, getAllDoctors, getHospitals } from "../services/doctorService";
import CustomSelect from "../components/Common/CustomSelect";

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  medicalRegistrationNumber: "",
  specialization: "",
  hospitalId: "",
  department: "",
  qualification: "",
  yearsOfExperience: "",
  licenseNumber: "",
  status: "Active" as "Active" | "Inactive",
  onboardingStatus: "not started" as "not started" | "in progress" | "completed",
};

const initialErrors: Record<string, string> = {};

const DoctorForm: React.FC = () => {
  const navigate = useNavigate();
  const { id: userId } = useParams<{ id: string }>();
  const isEdit = Boolean(userId);

  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState<Record<string, string>>(initialErrors);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [savedForm, setSavedForm] = useState(INITIAL_FORM);
  const [apiError, setApiError] = useState<string | null>(null);

  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [hospitalsLoading, setHospitalsLoading] = useState(true);

  // Load hospitals dropdown
  useEffect(() => {
    getHospitals()
      .then((data) => setHospitals(Array.isArray(data) ? data : []))
      .catch(() => toast.error("Failed to load hospitals dropdown list"))
      .finally(() => setHospitalsLoading(false));
  }, []);

  // Load existing doctor data for edit
  useEffect(() => {
    if (!isEdit || !userId) return;
    setFetching(true);
    getAllDoctors()
      .then((list) => {
        const doc = list.find((d: DoctorProfile) => String(d.userId) === userId);
        if (!doc) {
          toast.error("Doctor not found");
          navigate("/doctors");
          return;
        }
        const loaded = {
          firstName: doc.firstName || "",
          lastName: doc.lastName || "",
          email: doc.email || "",
          phoneNumber: doc.phoneNumber || "",
          medicalRegistrationNumber: doc.medicalRegistrationNumber || "",
          specialization: doc.specialization || "",
          hospitalId: doc.hospitalId ? String(doc.hospitalId) : "",
          department: doc.department || "",
          qualification: doc.qualification || "",
          yearsOfExperience:
            doc.yearsOfExperience !== null && doc.yearsOfExperience !== undefined
              ? String(doc.yearsOfExperience)
              : "",
          licenseNumber: doc.licenseNumber || doc.medicalLicenseNumber || "",
          status: doc.active ? ("Active" as const) : ("Inactive" as const),
          onboardingStatus: (doc.onboardingStatus || "not started") as "not started" | "in progress" | "completed",
        };
        setForm(loaded);
        setSavedForm(loaded);
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || "Failed to load doctor details";
        toast.error(msg);
        navigate("/doctors");
      })
      .finally(() => setFetching(false));
  }, [isEdit, userId, navigate]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    setApiError(null);
  };

  const handleReset = () => {
    setForm(savedForm);
    setErrors({});
    setApiError(null);
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};

    if (!form.firstName.trim()) errs.firstName = "First Name is required";
    else if (form.firstName.trim().length > 100) errs.firstName = "Max 100 characters allowed";

    if (!form.lastName.trim()) errs.lastName = "Last Name is required";
    else if (form.lastName.trim().length > 100) errs.lastName = "Max 100 characters allowed";

    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Invalid email format";

    if (!form.phoneNumber.trim()) errs.phoneNumber = "Phone Number is required";
    else if (!/^\+?[\d\s\-()]{7,20}$/.test(form.phoneNumber.trim()))
      errs.phoneNumber = "Invalid phone number format (7-20 digits)";

    if (!form.medicalRegistrationNumber.trim())
      errs.medicalRegistrationNumber = "Medical Registration Number is required";

    if (!form.specialization.trim()) errs.specialization = "Specialization is required";

    if (!form.hospitalId) errs.hospitalId = "Hospital selection is mandatory";

    if (!form.department.trim()) errs.department = "Department is required";

    if (!form.qualification.trim()) errs.qualification = "Qualification is required";

    if (form.yearsOfExperience === "" || form.yearsOfExperience === null) {
      errs.yearsOfExperience = "Years of Experience is required";
    } else {
      const exp = Number(form.yearsOfExperience);
      if (isNaN(exp) || exp < 0 || exp > 70) errs.yearsOfExperience = "Enter valid years of experience (0-70)";
    }

    if (!form.licenseNumber.trim()) errs.licenseNumber = "License Number is required";

    if (!form.status) errs.status = "Status selection is required";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);

    if (!validate()) {
      toast.error("Please fix errors in the form before saving.");
      return;
    }

    setLoading(true);
    try {
      const payload: CreateDoctorPayload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phoneNumber: form.phoneNumber.trim(),
        medicalRegistrationNumber: form.medicalRegistrationNumber.trim(),
        specialization: form.specialization.trim(),
        hospitalId: Number(form.hospitalId),
        department: form.department.trim(),
        qualification: form.qualification.trim(),
        yearsOfExperience: Number(form.yearsOfExperience),
        licenseNumber: form.licenseNumber.trim(),
        status: form.status,
        onboardingStatus: form.onboardingStatus,
      };

      let resData: any;
      if (isEdit && userId) {
        resData = await updateDoctorData(Number(userId), payload);
      } else {
        resData = await createDoctor(payload);
      }

      if (resData?.devEmailPreview) {
        console.log(
          "%c📧 [DEV/QA] Doctor Activation Email Triggered!",
          "background: #1e3a8a; color: #ffffff; font-weight: bold; padding: 4px 8px; border-radius: 4px;",
          resData.devEmailPreview
        );
      }

      if (isEdit) {
        if (form.onboardingStatus === "completed") {
          toast.success("Doctor profile updated & activation email sent to registered doctor!");
        } else {
          toast.success("Doctor profile updated successfully!");
        }
      } else {
        if (form.onboardingStatus === "completed") {
          toast.success("Doctor onboarded & activation email sent to registered doctor!");
        } else {
          toast.success("Doctor onboarded successfully!");
        }
      }
      navigate("/doctors");
    } catch (err: any) {
      console.error("Save doctor error:", err);
      const serverMessage =
        err?.response?.data?.message || err?.message || "Failed to save doctor";
      setApiError(serverMessage);
      toast.error(serverMessage);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-gray-500">
        <button onClick={() => navigate("/doctors")} className="hover:text-gray-900 transition-colors">
          Doctors
        </button>
        <span className="text-gray-400">/</span>
        <span className="text-gray-900 font-medium">{isEdit ? "Edit" : "Add New"}</span>
      </nav>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-5">
          {apiError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 font-medium">
              {apiError}
            </div>
          )}

          {/* Personal Information */}
          <div>
            <h2 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">Personal Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="First Name" name="firstName" value={form.firstName} onChange={handleChange} error={errors.firstName} required placeholder="e.g. John" />
              <FormField label="Last Name" name="lastName" value={form.lastName} onChange={handleChange} error={errors.lastName} required placeholder="e.g. Smith" />
              <FormField label="Email" name="email" value={form.email} onChange={handleChange} error={errors.email} required type="email" placeholder="e.g. dr.smith@hospital.org" />
              <FormField label="Phone Number" name="phoneNumber" value={form.phoneNumber} onChange={handleChange} error={errors.phoneNumber} required type="tel" placeholder="e.g. +1-555-0192" />
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Professional Details */}
          <div>
            <h2 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">Professional Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Medical Registration Number" name="medicalRegistrationNumber" value={form.medicalRegistrationNumber} onChange={handleChange} error={errors.medicalRegistrationNumber} required placeholder="e.g. MRN-987654" />
              <FormField label="License Number" name="licenseNumber" value={form.licenseNumber} onChange={handleChange} error={errors.licenseNumber} required placeholder="e.g. LIC-2024-88" />
              <FormField label="Specialization" name="specialization" value={form.specialization} onChange={handleChange} error={errors.specialization} required placeholder="e.g. Oncology / Hematology" />
              <FormField label="Department" name="department" value={form.department} onChange={handleChange} error={errors.department} required placeholder="e.g. Clinical Hematology" />
              <FormField label="Qualification" name="qualification" value={form.qualification} onChange={handleChange} error={errors.qualification} required placeholder="e.g. MBBS, MD, FRCP" />
              <FormField label="Years of Experience" name="yearsOfExperience" value={form.yearsOfExperience} onChange={handleChange} error={errors.yearsOfExperience} required type="number" placeholder="e.g. 8" />
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Assignment & Status */}
          <div>
            <h2 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">Assignment &amp; Status</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hospital <span className="text-red-500 ml-0.5">*</span>
                </label>
                {hospitalsLoading ? (
                  <div className="h-10 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center">
                    <Loader2 size={16} className="animate-spin text-gray-400" />
                  </div>
                ) : (
                  <CustomSelect
                    value={form.hospitalId}
                    onChange={(val) => {
                      setForm((prev) => ({ ...prev, hospitalId: val }));
                      if (errors.hospitalId) {
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.hospitalId;
                          return next;
                        });
                      }
                      setApiError(null);
                    }}
                    options={[
                      { value: "", label: "-- Select Hospital --" },
                      ...hospitals.map((h) => ({
                        value: String(h.id),
                        label: `${h.hospitalName} (${h.hospitalCode})`,
                      })),
                    ]}
                    error={!!errors.hospitalId}
                  />
                )}
                {errors.hospitalId && <p className="text-xs text-red-600 mt-1">{errors.hospitalId}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status <span className="text-red-500 ml-0.5">*</span>
                </label>
                <CustomSelect
                  value={form.status}
                  onChange={(val) => {
                    setForm((prev) => ({ ...prev, status: val as "Active" | "Inactive" }));
                    setApiError(null);
                  }}
                  options={[
                    { value: "Active", label: "Active" },
                    { value: "Inactive", label: "Inactive" },
                  ]}
                  error={!!errors.status}
                />
                {errors.status && <p className="text-xs text-red-600 mt-1">{errors.status}</p>}
              </div>
              {isEdit && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Onboarding Status
                  </label>
                  <CustomSelect
                    value={form.onboardingStatus}
                    onChange={(val) => setForm((prev) => ({ ...prev, onboardingStatus: val as any }))}
                    disabled={savedForm.onboardingStatus === "completed"}
                    options={
                      savedForm.onboardingStatus === "completed"
                        ? [{ value: "completed", label: "Completed" }]
                        : [
                            { value: "not started", label: "Not Started" },
                            { value: "in progress", label: "In Progress" },
                            { value: "completed", label: "Completed" },
                          ]
                    }
                  />
                  {savedForm.onboardingStatus === "completed" && (
                    <p className="mt-1 text-xs text-gray-500 font-medium">
                      🔒 Onboarding is completed and cannot be changed back to 'not started' or 'in progress'.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <hr className="border-gray-100" />
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleReset}
              disabled={loading}
              className="flex items-center justify-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors w-full sm:w-auto"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              onClick={() => navigate("/doctors")}
              disabled={loading}
              className="px-5 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors w-full sm:w-auto"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center justify-center gap-2 bg-primary-600 text-white px-5 py-2 rounded-lg text-sm font-medium shadow-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {isEdit ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>
                  <Save size={16} />
                  {isEdit ? "Update Doctor" : "Create Doctor"}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

// --- Helper Component ---

const FormField = ({
  label,
  name,
  value,
  onChange,
  error,
  required,
  type = "text",
  placeholder,
  className,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  error?: string;
  required?: boolean;
  type?: string;
  placeholder?: string;
  className?: string;
}) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
        error ? "border-red-300 bg-red-50" : "border-gray-200"
      }`}
    />
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

export default DoctorForm;